import { observer } from 'mobx-react-lite';
import type { EditorViewState } from 'owa-editor';
import { updateStickyDocking } from '../actions/calculateDocking';
import type DockingViewState from '../store/schema/DockingViewState';
import getScrollContainer from 'owa-editor/lib/utils/getScrollContainer';
import tryScrollIntoView from '../utils/tryScrollIntoView';
import * as React from 'react';

const styles = require('./StickyBottomBorder.scss');

interface StickyBottomBorderProps {
    editorViewState: EditorViewState;
    dockingViewState: DockingViewState;
    scrollContainerSelector?: string;
    className?: string;
}

const StickyBottomBorder = observer(function StickyBottomBorder(props: StickyBottomBorderProps) {
    const { editorViewState, dockingViewState, scrollContainerSelector, className } = props;

    let stickyBottomBorder = React.useRef<HTMLDivElement>(null);
    let intersectionObserver = React.useRef<IntersectionObserver>(null);
    let scrolledIntoView = React.useRef<boolean>(false);

    React.useEffect(() => {
        updateIntersectionObserver();

        return () => {
            updateIntersectionObserver();
        };
    }, []);

    const onStickyBottomBorderRef = (ref: HTMLDivElement) => {
        stickyBottomBorder.current = ref;
    };

    const updateIntersectionObserver = () => {
        const scrollContainer =
            scrollContainerSelector && stickyBottomBorder.current
                ? stickyBottomBorder.current.closest(scrollContainerSelector)
                : getScrollContainer(editorViewState);

        if (stickyBottomBorder.current && scrollContainer) {
            if (!intersectionObserver.current) {
                intersectionObserver.current = new IntersectionObserver(
                    (entries: IntersectionObserverEntry[]) => {
                        if (entries[0]) {
                            // The observed "sticky bottom border" is a 1px height DIV, when it is inside the view, intersectionRatio should be 1.
                            // If its value is not 1, it means this border is out of the view
                            const isOutOfView = entries[0].intersectionRatio != 1;
                            updateStickyDocking(
                                dockingViewState,
                                isOutOfView,
                                dockingViewState.dockingTriggerPart
                                    ? dockingViewState.dockingTriggerPart.offsetWidth
                                    : dockingViewState.dockingWidth
                            );
                            if (isOutOfView && !scrolledIntoView.current) {
                                tryScrollIntoView(editorViewState, dockingViewState);
                            }
                            scrolledIntoView.current = true;
                        }
                    },
                    {
                        rootMargin: '0px',
                        threshold: [1],
                        root: scrollContainer,
                    }
                );
            }
            intersectionObserver.current.observe(stickyBottomBorder.current);
        } else {
            if (intersectionObserver.current) {
                intersectionObserver.current.disconnect();
                intersectionObserver.current = null;
            }
        }
    };

    return (
        <div
            className={className ? className : styles.bottomBorder}
            ref={onStickyBottomBorderRef}
        />
    );
});

export default StickyBottomBorder;
